import React, { useEffect, useRef, useState } from "react";
import RichText from "components/rich-text";
import FocusTrap from "../../utils/focus-trap";

const RenderField = (fieldData, openKey, setOpenKey, setIsSubmit, setHasSelected, isContainer = false, reset = false) => {
    switch (fieldData?._type) {
    case "dropdown":
        return (
            <Dropdown
                data={fieldData}
                key={fieldData?._key}
                openKey={openKey}
                setOpenKey={setOpenKey}
                setIsSubmit={setIsSubmit}
                setHasSelected={setHasSelected}
                isContainer={isContainer}
                reset={reset}
            />
        );
    case "inputField":
        return <Input data={fieldData} key={fieldData?._key} isContainer={isContainer} />;
    case "richText":
        if (!isContainer) {
            return (
                <div className="contact-text" key={fieldData?._key}>
                    <RichText
                        value={fieldData?.data}
                        key={fieldData?._key}
                    />
                </div>
            );
        }
        return null;
    default:
        return null;
    }
};
const Input = ({ data, isContainer }) => {
    const handleChange = (event) => {
        const { target } = event;
        target.placeholder = "";
        target.classList.remove("error");
    };
    return (
        <div
            className={`${isContainer ? "appearance-none lg:w-1/4 md:w-1/2" : "lg:w-2/3 !mb-4"} w-full sm:pr-10 sm:pl-0 ${data?.type === "textarea" ? "mt-3" : "mb-9 sm:mb-10"}`}
            data-id={data?.name}
        >
            <label htmlFor={data?.name} className="block text-lg sm:text-[1.1875rem] text-gray-701 font-semibold mb-1.5 sm:mb-2.5">
                {data?.label}
                {data?.required && "*"}
            </label>
            {data?.type === "textarea" ? (
                <textarea className="h-30 contact-field" id={data?.name} name={data?.name} required={data?.required} onChange={handleChange} />
            ) : (<input className="contact-field" type={data?.type} id={data?.name} name={data?.name} required={data?.required} onChange={handleChange} />)}
        </div>
    ); 
};

const Dropdown = ({ data, openKey, setOpenKey, setIsSubmit, setHasSelected, isContainer, reset }) => {
    const [selected, setSelected] = useState(data?.options[0]?.expandable ? "" : data?.options[0]?.name);
    const [currentLabel, setCurrentLabel] = useState(data?.initOption);
    const [extendedFields, setExtendedFields] = useState([]);
    const [isReset, setIsReset] = useState(reset);
    const triggerElement = useRef(null);
    
    useEffect(() => {
        if (reset) {
            setExtendedFields([]);
            setSelected("");
            setCurrentLabel(data?.initOption);
        }
        setIsReset(false);
    }, [reset, isReset]);

    const removeErrorExtendedFields = (element) => {    
        const removeError = (node) => {
            if (node) {
                const errorFields = node.querySelectorAll("input.error, textarea.error");
                errorFields.forEach((field) => {
                    // eslint-disable-next-line no-param-reassign
                    field.placeholder = "";
                    // eslint-disable-next-line no-param-reassign
                    field.classList.remove("error");
                });
                removeError(node.nextSibling);
            }
        };
        removeError(element.nextSibling);
    };

    const handleOpen = () => setOpenKey(openKey === data?._key ? null : data?._key);

    const handleChange = ({ label, name, expandable, showSubmit, items = [] } = data) => {
        if (expandable) {
            if (selected === name) {
                setIsReset(true);
            } else {
                setSelected(name);
                setCurrentLabel(label);
                setExtendedFields(items);
            }
            setIsSubmit(showSubmit);
        } else {
            setSelected(name);
            setCurrentLabel(label);
        }
        setOpenKey(null);
        setHasSelected(true);
        removeErrorExtendedFields(triggerElement.current.parentNode.parentNode);
        triggerElement.current.focus();
    };

    return (
        <>
            <div className={`lg:w-1/3 sm:w-1/2 w-full sm:pr-10 sm:pl-0 ${isContainer ? "mb-4" : "mb-0"} sm:mb-0`} key={data?._key} data-id={data?.name}>
                <label htmlFor={data?.name} className="block text-lg sm:text-[1.1875rem] text-gray-701 font-semibold mb-1.5 sm:mb-2.5">
                    {data?.label}
                    {data?.required ? "*" : ""}
                </label>
                <select id={data?.name} name={data?.name} value={selected} className="hidden" onChange={() => null}>
                    {data?.extendable && (<option value="" disabled>{data?.initOption}</option>)}
                    {data?.options?.map((option) => (<option key={option?._key} value={option?.name}>{option?.label}</option>))}
                </select>
                <div className="relative">
                    <div
                        key={data?._key}
                        role="button"
                        ref={triggerElement}
                        tabIndex={0}
                        className={`contact-field hover:cursor-pointer py-3.5 px-6 sm:text-base contact-arrow ${openKey === data?._key ? "active" : ""}`}
                        onClick={() => handleOpen()}
                        onKeyDown={(event) => ((event.key === "Escape") ? setOpenKey(null) : null)}
                        aria-expanded={openKey === data?._key ? "true" : "false"}
                    >
                        {currentLabel}

                    </div>
                    <FocusTrap open={{ setOpen: setOpenKey, open: openKey === data?._key }} trigger={triggerElement}>
                        <div className={`absolute z-20 top-[103%] left-0 w-full ${openKey === data?._key ? "" : "hidden"}`}>
                            {data?.options?.map((option) => (
                                <div
                                    role="button"
                                    tabIndex={0}
                                    key={option?._key}
                                    className="contact-field hover:cursor-pointer py-3.5 px-6 sm:text-base border-b border-[#e5e5e5] hover:bg-[#c8c8c8] hover:border-[#c8c8c8]"
                                    onClick={() => handleChange(option)}
                                    onKeyDown={() => null}
                                >
                                    {option?.label}
                                </div>
                            ))}
                        </div>
                    </FocusTrap>
                </div>
            </div>
            {extendedFields?.map((item) => (
                RenderField(item, openKey, setOpenKey, setIsSubmit, setHasSelected, false, isReset)
            ))}
        </>
    );
};

export default RenderField;
